import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { OperationTypeFindInterface } from '@zaoblako/shared/modules/document/dtos/operation-type/operation-type-find.dto';
import {
  OperationTypeFormDto,
  OperationTypeFormInterface
} from '@zaoblako/shared/modules/document/dtos/operation-type/operation-type-form.dto';
import { OperationTypeMoveTypeEnum } from '@zaoblako/shared/modules/document/enums/operation.type.move.type.enum';
import { TaskKindEnum } from '@zaoblako/shared/modules/task/enum/task-kind.enum';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { OperationTypeService } from '@zaoblako/web/core/services/finance/operation-type.service';
import { DirectoryCommonHistoryComponent } from '@zaoblako/web/modules/directory/dumbs/directory-common-history/directory-common-history.component';
import { DirectoryCommonHistoryTypeEnum } from '@zaoblako/web/modules/directory/dumbs/directory-common-history/enums/directory-common-history-type.enum';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { AccountingAccountMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/accounting-account-multi-select/accounting-account-multi-select.component';
import { SingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/single-select/single-select.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { OperationTypeMoveTypeNamePipe } from '@zaoblako/web/shared/pipes/operation-type-move-type-name.pipe';
import { TaskKindNamePipe } from '@zaoblako/web/shared/pipes/task-kind-name.pipe';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import { CardModule } from 'primeng/card';
import { TagModule } from 'primeng/tag';
import { CheckboxModule } from 'primeng/checkbox';
import { Observable } from 'rxjs';

@Component({
  standalone: true,
  selector: 'crm-operation-type-form',
  templateUrl: './operation-type-form.component.html',
  imports: [
    CommonModule,
    CardModule,
    InputTextComponent,
    JoinPipe,
    SingleSelectComponent,
    OperationTypeMoveTypeNamePipe,
    ButtonComponent,
    SpinnerComponent,
    DirectoryCommonHistoryComponent,
    AccountingAccountMultiSelectComponent,
    TaskKindNamePipe,
    CheckboxModule,
    FormsModule,
    TagModule
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OperationTypeFormComponent extends BaseFormComponent<OperationTypeFormInterface> implements OnInit {
  @Input() public readonly index: number;
  public operationType: OperationTypeFormInterface = {
    name: '',
    type: OperationTypeMoveTypeEnum.EXCLUDE
  };

  public dto = OperationTypeFormDto;
  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public types: OperationTypeMoveTypeEnum[] = Object.values(OperationTypeMoveTypeEnum);
  public brsTaskKinds: TaskKindEnum[] = Object.values(TaskKindEnum).filter(
    (key) => key !== TaskKindEnum.PAYMENT_OF_SALARY
  );

  public get DirectoryCommonHistoryTypeEnum() {
    return DirectoryCommonHistoryTypeEnum;
  }

  public constructor(
    public readonly operationTypeService: OperationTypeService,
    public readonly formStoreService: FormStoreService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly errorService: ErrorService,
    private readonly confirmService: ConfirmDialogService
  ) {
    super();
  }

  public ngOnInit() {
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    }
  }

  public getById() {
    this.formStoreService.setLoading(true, this.index);

    this.operationTypeService
      .findById<OperationTypeFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId)
      .subscribe({
        next: (operationType) => {
          this.operationType = operationType;
          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        }
      });
  }

  public saveOperationType() {
    if (this.checkValidate(this.operationType).valid) {
      if (this.formStoreService.getState.forms[this.index]?.entityId) {
        this.formStoreService.setLoading(true, this.index);

        this.operationTypeService
          .update<OperationTypeFormInterface, OperationTypeFindInterface>(this.operationType)
          .subscribe({
            next: (operationType) => {
              this.formStoreService.setLoading(false, this.index);
              this.errorService.addSuccessMessage(`Группа видов операций "${this.operationType.name}" обновлена!`);
              this.formStoreService.saveNext(operationType, this.index);
            },
            error: () => {
              this.formStoreService.setLoading(false, this.index);
            }
          });
      } else {
        this.formStoreService.setLoading(true, this.index);

        this.operationTypeService.create<OperationTypeFindInterface>(this.operationType).subscribe({
          next: (operationType) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage('Группа видов операций создана!');
            this.formStoreService.saveNext(operationType, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  public deleteOperationType() {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить группу видов операций "${this.operationType.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.operationTypeService.deleteById(this.formStoreService.getState.forms[this.index]?.entityId).subscribe({
          next: () => {
            this.errorService.addSuccessMessage(`Группа видов операций "${this.operationType.name}" удалена!`);
            this.formStoreService.deleteNext(this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    });
  }

  public restoreOperationType() {
    this.confirmService.confirm({
      message: `Вы действительно хотите восстановить группу видов операций "${this.operationType.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.operationTypeService.restoreOne(this.formStoreService.getState.forms[this.index]?.entityId).subscribe({
          next: () => {
            this.operationType = {
              ...this.operationType,
              deleted: null
            } as OperationTypeFindInterface;
            this.cdRef.markForCheck();
            this.errorService.addSuccessMessage(`Группа видов операций "${this.operationType.name}" восстановлена!`);
            this.formStoreService.saveNext(this.operationType, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    });
  }

  public changeTaskKind() {
    if (!this.operationType.brsTaskKind) {
      this.operationType.brsTaskKind = null;
    }
  }

  public setUndistributed(): void {
    if (this.operationType.forUndistributed) {
      this.operationTypeService
        .makeForUndistributed(this.formStoreService.getState.forms[this.index]?.entityId)
        .subscribe();
    } else {
      this.operationTypeService
        .dropForUndistributed(this.formStoreService.getState.forms[this.index]?.entityId)
        .subscribe();
    }
  }
}
